import { Capacitor } from '@capacitor/core';

/**
 * Origin for API requests.
 *
 * Browser: '' (empty) — relative paths go through Vite proxy in dev,
 *                       same-origin in production.
 * Capacitor native: explicit origin, since the app is served from
 *                   capacitor://localhost and has no proxy.
 *
 * Override with VITE_API_ORIGIN (e.g. http://192.168.1.50:3001 for a real device
 * on LAN, or the deployed API URL for TestFlight builds).
 */
const override = import.meta.env.VITE_API_ORIGIN as string | undefined;

function defaultOrigin(): string {
  // Browser / same-origin: empty string (Vite proxy in dev, CloudFront in prod).
  if (!Capacitor.isNativePlatform()) return '';

  // Hosted Capacitor mode: app is loaded from a real domain (e.g.
  // https://dwfhu-emm.deltacloud365.com). Use same-origin so /api/* goes through
  // the same CloudFront that served the SPA.
  if (
    typeof window !== 'undefined' &&
    window.location?.hostname &&
    !/^(localhost|capacitor$|\d+\.\d+\.\d+\.\d+)/.test(window.location.hostname)
  ) {
    return '';
  }

  // Native+simulator falling back to Mac host API.
  return 'http://localhost:3001';
}

export const API_ORIGIN: string = override
  ? override.replace(/\/$/, '')
  : defaultOrigin();

/** Prefix `/api` path with the correct origin. */
export function apiUrl(path: string): string {
  return `${API_ORIGIN}/api${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Resolve a static asset URL to an absolute URL.
 *
 * Handles three formats:
 * - `s3:uploads/uuid.ext` → routed through `/api/uploads/resolve?key=...`
 *   which 302-redirects to a fresh pre-signed S3 URL.
 * - `https://...` (legacy pre-signed URLs still in DB) → passed through as-is.
 * - `/uploads/xxx.jpg` (local dev) → prefixed with API_ORIGIN for Capacitor.
 */
export function staticAssetUrl(path: string): string {
  if (!path) return path;
  // S3 key format: resolve on demand via backend redirect
  if (path.startsWith('s3:')) {
    const key = encodeURIComponent(path.slice(3));
    return apiUrl(`/uploads/resolve?key=${key}`);
  }
  // Legacy pre-signed URLs or any absolute URL — use directly
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_ORIGIN) return path;
  return `${API_ORIGIN}${path.startsWith('/') ? path : `/${path}`}`;
}
